import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {
  NgbActiveModal,
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';

let dialogInstanceCounter = 0;

@Component({
  selector: 'doc-ngb-dialog',
  standalone: true,
  imports: [CommonModule],
  template: `
    <ng-template #modalTpl let-modal>
      @if (showHeader !== false && (header || headerTemplate)) {
        <div class="modal-header doc-dialog__header">
          @if (headerTemplate) {
            <div class="doc-dialog__header-content">
              <ng-container *ngTemplateOutlet="headerTemplate"></ng-container>
            </div>
          } @else {
            <h5 class="modal-title doc-dialog__title">{{ header }}</h5>
          }
          @if (closable !== false) {
            <button
              type="button"
              class="doc-dialog-close"
              aria-label="Close"
              (click)="dismiss(modal)">
              <i class="pi pi-times" aria-hidden="true"></i>
            </button>
          }
        </div>
      }
      <div
        class="modal-body doc-dialog__body"
        [ngStyle]="contentStyle"
        (click)="onBodyClick($event)">
        <ng-content></ng-content>
      </div>
      @if (footerTemplate) {
        <div class="modal-footer doc-dialog__footer">
          <ng-container *ngTemplateOutlet="footerTemplate"></ng-container>
        </div>
      }
    </ng-template>
  `,
  styleUrl: './doc-ngb-dialog.component.scss',
})
export class DocNgbDialogComponent implements OnChanges, OnDestroy, AfterContentInit {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onHide = new EventEmitter<unknown>();

  @Input() header?: string;
  @Input() modal: boolean | string = true;
  @Input() closable = true;
  @Input() draggable = false;
  @Input() resizable = false;
  @Input() showHeader = true;
  @Input() dismissableMask = true;
  @Input() closeOnEscape = true;
  @Input() maximizable = false;
  @Input() style?: Record<string, string>;
  @Input() contentStyle?: Record<string, string>;
  @Input() styleClass?: string;
  @Input() baseZIndex?: number;
  @Input() appendTo?: string;
  @Input() scrollable = false;
  /** Optional size preset: sm | md | lg | xl (also accepts Bootstrap ngb sizes). */
  @Input() size?: 'sm' | 'md' | 'lg' | 'xl' | string;

  @ContentChild('dialogHeader', { read: TemplateRef })
  protected headerTemplate?: TemplateRef<unknown>;

  @ContentChild('dialogFooter', { read: TemplateRef })
  protected footerTemplate?: TemplateRef<unknown>;

  @ViewChild('modalTpl') private modalTpl!: TemplateRef<unknown>;

  private readonly modalService = inject(NgbModal);
  private modalRef: NgbModalRef | null = null;
  private readonly dialogClass = `doc-ngb-dialog-${++dialogInstanceCounter}`;
  private styleElement: HTMLStyleElement | null = null;
  private suppressHideEvent = false;

  ngAfterContentInit(): void {
    if (this.visible) {
      queueMicrotask(() => this.open());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['visible']) {
      return;
    }

    if (this.visible) {
      queueMicrotask(() => this.open());
      return;
    }

    this.dismissModal(false);
  }

  ngOnDestroy(): void {
    this.dismissModal(false);
    this.removeDialogStyles();
  }

  dismiss(modal: NgbActiveModal): void {
    modal.dismiss();
  }

  onBodyClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  private open(): void {
    if (!this.visible || this.modalRef || !this.modalTpl) {
      return;
    }

    const sizeClass =
      this.size === 'sm'
        ? 'doc-dialog--sm'
        : this.size === 'md'
          ? 'doc-dialog--md'
          : this.size === 'lg'
            ? 'doc-dialog--lg'
            : this.size === 'xl'
              ? 'doc-dialog--xl'
              : '';

    const options: NgbModalOptions = {
      centered: true,
      scrollable: this.scrollable,
      backdrop:
        this.modal === false
          ? false
          : this.dismissableMask === false
            ? 'static'
            : true,
      keyboard: this.closable !== false && this.closeOnEscape !== false,
      modalDialogClass: ['doc-dialog', this.styleClass, this.dialogClass, sizeClass]
        .filter(Boolean)
        .join(' '),
      windowClass: [
        'doc-dialog-window',
        this.baseZIndex ? `doc-ngb-dialog-window-${this.dialogClass}` : '',
      ]
        .filter(Boolean)
        .join(' '),
      backdropClass: [
        'doc-dialog-backdrop',
        this.baseZIndex ? `doc-ngb-dialog-backdrop-${this.dialogClass}` : '',
      ]
        .filter(Boolean)
        .join(' '),
    };

    const width = this.style?.['width'] ?? this.style?.['maxWidth'];
    if (!this.size) {
      if (width?.includes('50') || width?.includes('60')) {
        options.size = 'lg';
      }
      if (width?.includes('80') || width?.includes('90')) {
        options.size = 'xl';
      }
    } else if (this.size === 'sm' || this.size === 'lg' || this.size === 'xl') {
      options.size = this.size;
    }

    this.applyDialogStyles();
    this.modalRef = this.modalService.open(this.modalTpl, options);

    this.modalRef.closed.subscribe(() => this.handleHide());
    this.modalRef.dismissed.subscribe(() => this.handleHide());
  }

  private dismissModal(emitHide: boolean): void {
    if (!this.modalRef) {
      return;
    }

    this.suppressHideEvent = !emitHide;
    const ref = this.modalRef;
    this.modalRef = null;
    ref.dismiss();
  }

  private handleHide(): void {
    this.modalRef = null;
    this.removeDialogStyles();

    if (this.suppressHideEvent) {
      this.suppressHideEvent = false;
      return;
    }

    if (this.visible) {
      this.visible = false;
      this.visibleChange.emit(false);
    }

    this.onHide.emit();
  }

  private applyDialogStyles(): void {
    this.removeDialogStyles();

    const rules: string[] = [];
    if (this.style && Object.keys(this.style).length) {
      const styleText = Object.entries(this.style)
        .map(([key, value]) => `${this.camelToKebab(key)}: ${value}`)
        .join('; ');
      rules.push(`.${this.dialogClass} { ${styleText} }`);
    }

    if (this.baseZIndex) {
      rules.push(
        `.doc-ngb-dialog-window-${this.dialogClass} { z-index: ${this.baseZIndex} !important; }`
      );
      rules.push(
        `.doc-ngb-dialog-backdrop-${this.dialogClass} { z-index: ${this.baseZIndex - 10} !important; }`
      );
    }

    if (this.contentStyle?.['overflow'] === 'visible' || this.contentStyle?.['overflowY'] === 'visible') {
      rules.push(
        `.doc-ngb-dialog-window-${this.dialogClass} .modal-content, .doc-ngb-dialog-window-${this.dialogClass} .modal-body { overflow: visible !important; }`
      );
    }

    if (!rules.length) {
      return;
    }

    this.styleElement = document.createElement('style');
    this.styleElement.textContent = rules.join('\n');
    document.head.appendChild(this.styleElement);
  }

  private removeDialogStyles(): void {
    this.styleElement?.remove();
    this.styleElement = null;
  }

  private camelToKebab(value: string): string {
    return value.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
  }
}
